import { useState, useMemo } from 'react';
import { projectPartnership, projectRecurringLiability } from '@affily/shared/projection';
import { describeTerms } from '@affily/shared/commission';
import { formatCents, formatCount, formatPercent, formatEpc, percentToBps } from '@affily/shared/money';
import { Field, Input, Select, Unknown } from './ui.jsx';

/**
 * The shared calculator core.
 *
 * Both calculators — the creator's earnings one and the brand's cost one — run
 * the same `projectPartnership()` over the same terms. They differ only in
 * which inputs they ask for and which outputs they lead with, so the two can
 * be shown side by side on a program page without ever contradicting each
 * other.
 */

export const DEFAULT_TERMS = {
  commissionType: 'percent_of_sale',
  ratePercent: '20',
  flatAmount: '25',
  cookieWindowDays: 30,
  recurringMode: 'one_time',
  recurringMonths: '12',
};

/** Build real terms from the form's string state. */
export function toTerms(form) {
  const isPercent = form.commissionType === 'percent_of_sale';
  return {
    commissionType: form.commissionType,
    rateBps: isPercent ? percentToBps(Number(form.ratePercent || 0)) : null,
    flatAmountCents: isPercent ? null : Math.round(Number(form.flatAmount || 0) * 100),
    cookieWindowDays: Number(form.cookieWindowDays || 30),
    recurringMode: form.recurringMode,
    recurringMonths:
      form.recurringMode === 'fixed_period' ? Number(form.recurringMonths || 1) : null,
  };
}

/** Terms inputs, shared by both calculators. */
export function TermsInputs({ form, set, locked = false, className = '' }) {
  const isPercent = form.commissionType === 'percent_of_sale';

  if (locked) {
    return (
      <div className={`rounded-lg border border-rule bg-paper-sunken p-3 ${className}`}>
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
          This program’s terms
        </p>
        <p className="mt-1 font-semibold tabular-nums text-accent">{describeTerms(toTerms(form))}</p>
      </div>
    );
  }

  return (
    <div className={`grid gap-4 sm:grid-cols-2 ${className}`}>
      <Field label="Commission type">
        <Select value={form.commissionType} onChange={set('commissionType')}>
          <option value="percent_of_sale">% of sale</option>
          <option value="flat_per_conversion">Flat per conversion</option>
        </Select>
      </Field>

      {isPercent ? (
        <Field label="Rate (%)">
          <Input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={form.ratePercent}
            onChange={set('ratePercent')}
          />
        </Field>
      ) : (
        <Field label="Amount per conversion (USD)">
          <Input
            type="number"
            min="0"
            step="1"
            value={form.flatAmount}
            onChange={set('flatAmount')}
          />
        </Field>
      )}

      <Field label="Payout model">
        <Select value={form.recurringMode} onChange={set('recurringMode')}>
          <option value="one_time">One-time</option>
          <option value="fixed_period">Recurring</option>
        </Select>
      </Field>

      {form.recurringMode === 'fixed_period' && (
        <Field label="Billing cycles">
          <Input
            type="number"
            min="1"
            max="60"
            value={form.recurringMonths}
            onChange={set('recurringMonths')}
          />
        </Field>
      )}
    </div>
  );
}

/** Run a projection from form state, tolerating half-typed input. */
export function useProjection(form, assumptions) {
  return useMemo(() => {
    try {
      const terms = toTerms(form);
      return {
        terms,
        projection: projectPartnership(terms, assumptions),
        liability: projectRecurringLiability(terms, assumptions),
      };
    } catch {
      // Mid-typing the form is briefly invalid. Show dashes, not an error.
      return { terms: null, projection: null, liability: null };
    }
  }, [form, assumptions]);
}

export function ResultRow({ label, value, hint, emphasis = false, empty }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-rule py-2 last:border-0">
      <span>
        <span className={`block text-sm ${emphasis ? 'font-medium text-ink' : 'text-ink-muted'}`}>
          {label}
        </span>
        {hint && <span className="block text-xs text-ink-faint">{hint}</span>}
      </span>
      <span
        className={`shrink-0 tabular-nums ${
          emphasis ? 'text-xl font-semibold text-accent' : 'font-medium text-ink'
        }`}
      >
        {value ?? <Unknown reason={empty} />}
      </span>
    </div>
  );
}

/** The retention input, with an honest default and a plain explanation. */
export function RetentionField({ value, onChange, audience = 'creator' }) {
  return (
    <Field
      label="Monthly retention (%)"
      hint={
        audience === 'brand'
          ? 'What share of subscribers survive each month. 100% is your maximum exposure, not a forecast.'
          : 'What share of customers stay each month. 100% is a best case — most SaaS sits nearer 90%.'
      }
    >
      <Input type="number" min="0" max="100" step="1" value={value} onChange={onChange} />
    </Field>
  );
}

/** Shared footnote — the fee, stated the same way to both sides. */
export function FeeNote({ audience }) {
  return (
    <p className="mt-4 border-t border-rule pt-3 text-xs text-ink-faint">
      {audience === 'brand'
        ? 'The figures above are the commissions themselves. Affily’s fee is shown in your account once you sign up.'
        : 'The figures above are the commissions themselves. Affily’s fee is shown in your account once you sign up, before you’re paid.'}{' '}
      These are projections from your own assumptions — the same maths the live dashboards use, but
      applied to numbers you supplied rather than traffic we measured.
    </p>
  );
}

export { formatCents, formatCount, formatPercent, formatEpc, useState };
